import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Tooltip,
  CircularProgress,
  Autocomplete,
  IconButton
} from '@mui/material';
import {
  Clear,
  CloudUpload,
  UploadFile,
  HelpOutline
} from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useTheme } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { DraggableDialog } from '@/shared/components/DraggableDialog';
import { usePanelStyles } from '@/styles/utilities/hooks';
import CompactUploadDropzone from '../components/CompactUploadDropzone';
import { traverseFileTree } from '../utils/fileHelpers';

/**
 * UploadPanel Component
 * 
 * Resizable bottom panel for uploading PDB files with metadata.
 * Includes drag-and-drop, file browsing, and batch metadata entry.
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the panel is open
 * @param {Function} props.onToggle - Callback to toggle panel open/closed
 * @param {number} props.height - Panel height in pixels
 * @param {Function} props.onHeightChange - Callback when height changes
 * @param {Array} props.uploadedFiles - Array of uploaded file objects
 * @param {Function} props.onFilesUploaded - Callback when files are added
 * @param {Function} props.onClearFiles - Callback to clear all files
 * @param {Set} props.selectedFileIds - Set of selected file IDs
 * @param {Function} props.onSelectionChange - Callback when selection changes
 * @param {string} props.uploadProject - Project value for upload
 * @param {Function} props.onProjectChange - Callback when project changes
 * @param {string} props.uploadTarget - Target value for upload
 * @param {Function} props.onTargetChange - Callback when target changes
 * @param {string} props.uploadBatch - Batch value for upload
 * @param {Function} props.onBatchChange - Callback when batch changes
 * @param {Object} props.suggestions - Autocomplete suggestions for fields
 * @param {Function} props.onSubmit - Callback to submit upload
 * @param {Function} props.onCancel - Callback to cancel upload
 * @param {boolean} props.isUploading - Whether upload is in progress
 * @param {Array} props.columns - Column definitions for the file grid
 * @param {Object} props.columnVisibilityModel - Column visibility state
 * @param {Function} props.onColumnVisibilityChange - Callback for column visibility
 * @param {Function} props.onFileClick - Callback when a file row is clicked
 */
export default function UploadPanel({
  isOpen,
  onToggle,
  height,
  onHeightChange,
  uploadedFiles,
  onFilesUploaded,
  onClearFiles,
  selectedFileIds,
  onSelectionChange,
  uploadProject,
  onProjectChange,
  uploadTarget,
  onTargetChange,
  uploadBatch,
  onBatchChange,
  suggestions = { project: [], target: [], batch: [] },
  onSubmit,
  onCancel,
  isUploading,
  columns,
  columnVisibilityModel,
  onColumnVisibilityChange,
  onFileClick
}) {
  const theme = useTheme();
  const panelStyles = usePanelStyles();
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isDraggingHandle, setIsDraggingHandle] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Handle panel resize
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    setIsDraggingHandle(true);
    const startY = e.clientY;
    const startHeight = height;
    
    const handleMouseMove = (e) => {
      const deltaY = startY - e.clientY;
      const newHeight = Math.min(Math.max(200, startHeight + deltaY), window.innerHeight - 300);
      onHeightChange(newHeight);
    };
    
    const handleMouseUp = () => {
      setIsDraggingHandle(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [height, onHeightChange]);

  // Handle file drop
  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    
    // Collect all entries synchronously first
    const entries = [];
    const items = Array.from(e.dataTransfer.items);
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry();
        if (entry) {
          entries.push(entry);
        }
      }
    }
    
    // Process entries asynchronously
    const files = [];
    for (const entry of entries) {
      await traverseFileTree(entry, '', files);
    }
    
    if (files.length > 0) {
      onFilesUploaded(files);
    } else {
      // Fallback to regular file handling
      const regularFiles = Array.from(e.dataTransfer.files);
      if (regularFiles.length > 0) {
        onFilesUploaded(regularFiles);
      }
    }
  }, [onFilesUploaded]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Help Dialog */}
      <DraggableDialog
        open={showHelp}
        onClose={() => setShowHelp(false)}
        title="Upload Files Help"
        variant="help"
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{
          '& h3': { fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semibold, mt: theme.spacing(2), mb: theme.spacing(1) },
          '& h3:first-of-type': { mt: 0 },
          '& ul': { pl: theme.spacing(2), my: theme.spacing(0.5) },
          '& li': { fontSize: theme.typography.fontSize.xs, mb: theme.spacing(0.5) },
          '& code': {
            backgroundColor: theme.palette.grey[200],
            px: theme.spacing(0.5),
            py: theme.spacing(0.2),
            borderRadius: theme.spacing(0.5),
            fontSize: theme.typography.fontSize.xs,
            fontFamily: 'monospace'
          },
          '& p': { fontSize: theme.typography.fontSize.xs, my: theme.spacing(0.5) },
          '& strong': { fontWeight: theme.typography.fontWeight.semibold }
        }}>
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>
{`### Quick Upload Guide

Follow these simple steps to upload your PDB files:

### 1. Add Your Files
- **Drag and drop** PDB files or folders onto the upload area
- Or **click the upload area** to browse for files
- Files will appear in the grid

### 2. Set Required Metadata
**These three fields are essential:**
- **Project**: Select from Browser panel or type a new one
- **Target**: Select from Browser panel or type a new one
- **Batch**: Enter a **new, unique batch name** to distinguish this upload

*Example: If previous batches were "batch1", "batch2", use "batch3" or a descriptive name like "2024-01-mutants"*

### 3. Upload
- Click the **"Upload"** button (top right)
- Files will be processed and added to the main table

### After Upload
Fill in the important fields using the input panels:
- **Origin** and **Ligand** fields should be filled in
- Use **Pattern Extraction** panel to extract these from file paths
- Use **Metadata Editing** panel to bulk edit any fields
- Use **Find and Replace** panel to search and update text across fields
- Other fields are optional and can be added as needed`}
          </ReactMarkdown>
        </Box>
      </DraggableDialog>

      {/* Drag Handle */}
      <Box
        onMouseDown={handleDragStart}
        sx={{
          position: 'absolute',
          bottom: height + parseInt(theme.spacing(5)) - 4, // Account for toolbar height
          left: 0,
          right: 0,
          height: theme.spacing(0.5), // Thinner resize bar (4px at standard density)
          cursor: isDraggingHandle ? 'ns-resize' : 'ns-resize',
          backgroundColor: isDraggingHandle ? 'primary.main' : 'divider',
          '&:hover': {
            backgroundColor: 'primary.main'
          },
          zIndex: 1100
        }}
      />

      {/* Panel Content */}
      <Box
        sx={{
          position: 'absolute',
          bottom: theme.spacing(5), // Above the fixed toolbar
          left: 0,
          right: 0,
          height: `${height}px`,
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Loading Overlay */}
        {isUploading && uploadedFiles.length > 10 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: theme.palette.overlay.light,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="h6" sx={{ mt: theme.spacing(2), fontSize: theme.typography.fontSize.base }}>
              Uploading {uploadedFiles.length} files...
            </Typography>
            <Typography variant="body2" sx={{ mt: theme.spacing(1), fontSize: theme.typography.fontSize.sm, color: 'text.secondary' }}>
              Please wait while your files are being processed
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header Bar with Controls */}
          <Box sx={{ 
            display: 'flex', 
            gap: theme.spacing(1.5), 
            alignItems: 'center', 
            p: theme.spacing(1.5),
            height: theme.dimensions.panel.headerHeight,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
            overflowX: 'auto',
            overflowY: 'hidden',
            '&::-webkit-scrollbar': {
              height: theme.spacing(0.5)
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.grey[400],
              borderRadius: theme.spacing(0.25)
            }
          }}>
            {/* Title with Help button */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                sx={{
                  ...panelStyles.headerTitle,
                  whiteSpace: 'nowrap',
                  minWidth: 'fit-content'
                }}
              >
                Upload Files ({uploadedFiles.length})
              </Typography>
              <Tooltip title="Show help">
                <IconButton
                  size="small"
                  onClick={() => setShowHelp(true)}
                  sx={{
                    padding: theme.spacing(0.5),
                    '& .MuiSvgIcon-root': {
                      fontSize: theme.dimensions.icon.small
                    }
                  }}
                >
                  <HelpOutline />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Compact Dropzone with Clear button */}
            {uploadedFiles.length > 0 && (
              <Box sx={{ display: 'flex', gap: theme.spacing(0.5), alignItems: 'center' }}>
                <Box sx={{ width: theme.spacing(20), height: theme.dimensions.button.small }}>
                  <CompactUploadDropzone onFilesUploaded={onFilesUploaded} />
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Clear />}
                  onClick={onClearFiles}
                  disabled={uploadedFiles.length === 0 || isUploading}
                  sx={{ 
                    minWidth: theme.spacing(7.5)
                  }}
                >
                  Clear
                </Button>
              </Box>
            )}
            
            {/* Spacer */}
            <Box sx={{ flex: 1 }} />
            
            {/* Input Fields */}
            <Autocomplete
              options={suggestions.project}
              value={uploadProject}
              onInputChange={(event, newValue) => onProjectChange(newValue)}
              freeSolo
              size="small"
              renderOption={(props, option) => (
                <li {...props} style={{ fontSize: theme.typography.fontSize.sm }}>
                  {option}
                </li>
              )}
              renderInput={(params) => (
                <Tooltip title={uploadProject || ''} enterDelay={500} placement="bottom">
                  <TextField
                    {...params}
                    label="Project"
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        fontSize: theme.typography.fontSize.sm,
                        height: theme.dimensions.button.small
                      }
                    }}
                    InputLabelProps={{
                      shrink: true,
                      sx: { 
                        fontSize: theme.typography.fontSize.sm,
                        transform: 'translate(14px, -6px) scale(0.75)'
                      }
                    }}
                    sx={{ 
                      width: theme.spacing(20)
                    }}
                  />
                </Tooltip>
              )}
            />
            <Autocomplete
              options={suggestions.target}
              value={uploadTarget}
              onInputChange={(event, newValue) => onTargetChange(newValue)}
              freeSolo
              size="small"
              renderOption={(props, option) => (
                <li {...props} style={{ fontSize: theme.typography.fontSize.sm }}>
                  {option}
                </li>
              )}
              renderInput={(params) => (
                <Tooltip title={uploadTarget || ''} enterDelay={500} placement="bottom">
                  <TextField
                    {...params}
                    label="Target"
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        fontSize: theme.typography.fontSize.sm,
                        height: theme.dimensions.button.small
                      }
                    }}
                    InputLabelProps={{
                      shrink: true,
                      sx: { 
                        fontSize: theme.typography.fontSize.sm,
                        transform: 'translate(14px, -6px) scale(0.75)'
                      }
                    }}
                    sx={{ 
                      width: theme.spacing(20)
                    }}
                  />
                </Tooltip>
              )}
            />
            <Autocomplete
              options={suggestions.batch}
              value={uploadBatch}
              onInputChange={(event, newValue) => onBatchChange(newValue)}
              freeSolo
              size="small"
              renderOption={(props, option) => (
                <li {...props} style={{ fontSize: theme.typography.fontSize.sm }}>
                  {option}
                </li>
              )}
              renderInput={(params) => (
                <Tooltip title={uploadBatch || ''} enterDelay={500} placement="bottom">
                  <TextField
                    {...params}
                    label="Batch"
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        fontSize: theme.typography.fontSize.sm,
                        height: theme.dimensions.button.small
                      }
                    }}
                    InputLabelProps={{
                      shrink: true,
                      sx: { 
                        fontSize: theme.typography.fontSize.sm,
                        transform: 'translate(14px, -6px) scale(0.75)'
                      }
                    }}
                    sx={{ 
                      width: theme.spacing(20)
                    }}
                  />
                </Tooltip>
              )}
            />

            {/* Right spacer */}
            <Box sx={{ flex: 1 }} />
            
            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: theme.spacing(0.5) }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Clear />}
                onClick={onCancel}
                disabled={uploadedFiles.length === 0 || isUploading}
                sx={{ 
                  minWidth: theme.spacing(7.5)
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={isUploading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <CloudUpload />}
                onClick={onSubmit}
                disabled={uploadedFiles.length === 0 || isUploading}
                sx={{ 
                  minWidth: theme.spacing(10)
                }}
              >
                {isUploading ? 'Uploading...' : 'Upload'}
              </Button>
            </Box>
          </Box>

          {/* Content Area */}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            {uploadedFiles.length === 0 ? (
              // Large dropzone when no files
              <Box
                onClick={() => document.getElementById('pdb-directory-input-large').click()}
                onDrop={handleDrop}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDraggingOver(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setIsDraggingOver(false);
                  }
                }}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed',
                  borderColor: isDraggingOver ? 'primary.main' : 'grey.300',
                  borderRadius: 1,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  backgroundColor: isDraggingOver ? 'action.hover' : 'background.default',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <input
                  type="file"
                  webkitdirectory=""
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    onFilesUploaded(files);
                  }}
                  style={{ display: 'none' }}
                  id="pdb-directory-input-large"
                />
                <CloudUpload sx={{ fontSize: theme.spacing(6), color: isDraggingOver ? 'primary.main' : theme.palette.grey[400], mb: theme.spacing(2) }} />
                <Typography variant="h6" color={isDraggingOver ? "primary" : "text.secondary"} gutterBottom>
                  {isDraggingOver ? 'Release to upload' : 'Drop PDB files or folders here'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  or click to browse
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: theme.spacing(2) }}>
                  Supports .pdb files and directory upload
                </Typography>
              </Box>
            ) : (
              // Data grid when files are uploaded
              <DataGridPro
                rows={uploadedFiles}
                columns={columns}
                rowHeight={theme.dimensions.dataGrid.rowHeight}
                columnHeaderHeight={theme.dimensions.dataGrid.headerHeight}
                autoHeight={false}
                hideFooter
                getRowId={(row) => row.id}
                rowSelectionModel={{ ids: selectedFileIds, type: 'include' }}
                onRowSelectionModelChange={onSelectionChange}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={onColumnVisibilityChange}
                onRowClick={onFileClick}
                sx={{ 
                  height: '100%',
                  border: 'none',
                  '& .MuiDataGrid-cell': {
                    fontSize: theme.typography.fontSize.xs,
                    px: theme.spacing(1),
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    fontSize: theme.typography.fontSize.xs,
                    fontWeight: theme.typography.fontWeight.semibold,
                    minHeight: `${theme.dimensions.dataGrid.headerHeight}px !important`,
                    maxHeight: `${theme.dimensions.dataGrid.headerHeight}px !important`
                  },
                  '& .MuiDataGrid-columnHeader': {
                    px: theme.spacing(1)
                  },
                  '& .MuiDataGrid-row': {
                    minHeight: `${theme.dimensions.dataGrid.rowHeight}px !important`,
                    maxHeight: `${theme.dimensions.dataGrid.rowHeight}px !important`
                  },
                  '& .MuiDataGrid-cellContent': {
                    lineHeight: 1,
                    letterSpacing: '0'
                  },
                  '& .MuiDataGrid-virtualScrollerContent': {
                    paddingTop: '0 !important'
                  }
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}